/**
 * Streak achievement tiers, mirroring the student app's ladder
 * (apps/b2b-mobile/src/features/report/constants/streakAchievements.ts). The two apps must agree
 * on thresholds and colors — a teacher and their student are looking at the same achievement.
 *
 * Two distinct uses of the same ladder, which must not be mixed up:
 *  1. Earned/permanent tier — getEarnedTier(longestStreak). Never revoked when a streak breaks.
 *  2. Live progression — getStreakAchievementProgress(currentStreak). Resets with the streak.
 */

export interface AchievementTier {
  tier: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  accent: string;
  background: string;
}

export const ACHIEVEMENT_TIERS: readonly AchievementTier[] = [
  { tier: 1, accent: "#AAAAFF", background: "#D6D6FF" },
  { tier: 2, accent: "#A88BF7", background: "#E3D9FC" },
  { tier: 3, accent: "#B472EA", background: "#ECDBFA" },
  { tier: 4, accent: "#C25ED8", background: "#F7D2FF" },
  { tier: 5, accent: "#D8518C", background: "#F8DEE9" },
  { tier: 6, accent: "#D97343", background: "#F8E6DD" },
  { tier: 7, accent: "#CFDA36", background: "#FBFFC4" },
] as const;

export const STREAK_TIER_THRESHOLDS: Record<AchievementTier["tier"], number> = {
  1: 3,
  2: 7,
  3: 14,
  4: 30,
  5: 60,
  6: 100,
  7: 180,
};

const TIER_ORDER: readonly AchievementTier["tier"][] = [1, 2, 3, 4, 5, 6, 7];

/** Fill color for the progress bar before any tier has been earned (0–2 day streak). */
export const NEUTRAL_ACHIEVEMENT_FILL = "rgba(255, 255, 255, 0.55)";

/** Only throws if the fixed 1–7 set above is ever trimmed. */
export function getAchievementTier(tier: AchievementTier["tier"]): AchievementTier {
  const found = ACHIEVEMENT_TIERS.find((item) => item.tier === tier);
  if (!found) throw new Error(`No achievement tier token for tier ${tier}`);
  return found;
}

/** The highest tier this streak length has earned, or null below Tier 1 (3 days). */
export function getEarnedTier(streakDays: number): AchievementTier["tier"] | null {
  let earned: AchievementTier["tier"] | null = null;
  for (const tier of TIER_ORDER) {
    if (streakDays >= STREAK_TIER_THRESHOLDS[tier]) earned = tier;
    else break;
  }
  return earned;
}

/** Tier 1 when nothing is earned yet, null once Tier 7 is — there is no Tier 8. */
export function getNextTier(
  earnedTier: AchievementTier["tier"] | null
): AchievementTier["tier"] | null {
  if (earnedTier === 7) return null;
  return ((earnedTier ?? 0) + 1) as AchievementTier["tier"];
}

export interface StreakAchievementProgress {
  highestEarnedTier: AchievementTier["tier"] | null;
  nextTier: AchievementTier["tier"];
  nextThreshold: number;
}

/** Null once Tier 7 is reached — the bar is hidden rather than shown maxed out. */
export function getStreakAchievementProgress(streakDays: number): StreakAchievementProgress | null {
  const highestEarnedTier = getEarnedTier(streakDays);
  const nextTier = getNextTier(highestEarnedTier);
  if (nextTier === null) return null;

  return { highestEarnedTier, nextTier, nextThreshold: STREAK_TIER_THRESHOLDS[nextTier] };
}
